import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import { getHomeCarouselSolutions } from "./home-content";
import { SolutionCard } from "./SolutionCard";

type Locale = "fr" | "en";

/**
 * Premium catalog carousel — 3+peek desktop / 2+peek tablet / 1+peek mobile.
 * No autoplay. Preserves approved SolutionCard visual system.
 */
export function PremiumSolutionsCarousel({
  locale,
  title,
  limitedLabel,
  prevLabel,
  nextLabel,
}: {
  locale: Locale;
  title: ReactNode;
  limitedLabel: string;
  prevLabel: string;
  nextLabel: string;
}) {
  const solutions = getHomeCarouselSolutions();
  const trackRef = useRef<HTMLUListElement>(null);
  const [index, setIndex] = useState(0);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(true);
  const labelId = useId();
  const total = solutions.length;

  const syncFromScroll = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const slides = [...track.querySelectorAll<HTMLElement>(".pep-home-carousel__slide")];
    if (slides.length === 0) return;

    const left = track.scrollLeft;
    let closest = 0;
    let best = Number.POSITIVE_INFINITY;
    slides.forEach((slide, i) => {
      const dist = Math.abs(slide.offsetLeft - left);
      if (dist < best) {
        best = dist;
        closest = i;
      }
    });

    const maxScroll = track.scrollWidth - track.clientWidth;
    setIndex(closest);
    setCanPrev(left > 4);
    setCanNext(left < maxScroll - 4);
  }, []);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    syncFromScroll();
    track.addEventListener("scroll", syncFromScroll, { passive: true });
    const ro = new ResizeObserver(() => syncFromScroll());
    ro.observe(track);
    return () => {
      track.removeEventListener("scroll", syncFromScroll);
      ro.disconnect();
    };
  }, [syncFromScroll]);

  const scrollToIndex = useCallback((nextIndex: number) => {
    const track = trackRef.current;
    if (!track) return;
    const slides = track.querySelectorAll<HTMLElement>(".pep-home-carousel__slide");
    const target = slides[Math.max(0, Math.min(nextIndex, slides.length - 1))];
    if (!target) return;
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    track.scrollTo({ left: target.offsetLeft, behavior: reduceMotion ? "auto" : "smooth" });
  }, []);

  const goPrev = () => scrollToIndex(index - 1);
  const goNext = () => scrollToIndex(index + 1);

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      goPrev();
    } else if (event.key === "ArrowRight") {
      event.preventDefault();
      goNext();
    } else if (event.key === "Home") {
      event.preventDefault();
      scrollToIndex(0);
    } else if (event.key === "End") {
      event.preventDefault();
      scrollToIndex(total - 1);
    }
  };

  const positionLabel = `${index + 1} / ${total}`;

  return (
    <div
      className="pep-home-carousel"
      data-carousel="premium-solutions"
      onKeyDown={onKeyDown}
    >
      <div className="pep-home-carousel__header">
        <div className="pep-home-carousel__title">{title}</div>
        <div className="pep-home-carousel__nav" role="group" aria-labelledby={labelId}>
          <p id={labelId} className="visually-hidden">
            {locale === "en"
              ? "ITaaS Suite solutions carousel. Use arrow keys or buttons to browse."
              : "Carrousel des solutions ITaaS Suite. Utilisez les flèches ou les boutons pour parcourir."}
          </p>
          <button
            type="button"
            className="pep-home-carousel__btn"
            onClick={goPrev}
            disabled={!canPrev}
            aria-label={prevLabel}
          >
            <span aria-hidden="true">←</span>
          </button>
          <span className="pep-home-carousel__pos" aria-live="polite">
            {positionLabel}
          </span>
          <button
            type="button"
            className="pep-home-carousel__btn"
            onClick={goNext}
            disabled={!canNext}
            aria-label={nextLabel}
          >
            <span aria-hidden="true">→</span>
          </button>
        </div>
      </div>

      <ul
        ref={trackRef}
        className="pep-home-carousel__track list-unstyled mb-0"
        tabIndex={0}
        aria-roledescription="carousel"
        aria-label={locale === "en" ? "Solutions catalog" : "Catalogue des solutions"}
      >
        {solutions.map((teaser, i) => (
          <li
            key={teaser.slug}
            className="pep-home-carousel__slide"
            aria-label={`${i + 1} / ${total}`}
          >
            <SolutionCard teaser={teaser} locale={locale} limitedLabel={limitedLabel} />
          </li>
        ))}
      </ul>
    </div>
  );
}
